<template>
    <v-dialog v-model="dialog" v-stack="{ open: dialog, close: hide }" persistent no-click-animation scrollable
        max-width="920" content-class="aed-dialog">
        <div class="aed-modal">
            <!-- HEADER -->
            <div class="aed-hdr">
                <button type="button" class="aed-close" @click="hide">
                    <v-icon size="14">mdi-close</v-icon>
                </button>
                <span class="aed-title">{{ editMode ? $t('common.edit_exercise') : $t('common.add_exercise') }}</span>
                <div class="aed-hdr-actions">
                    <button-tooltip bottom :tooltip="$t('tooltips.copy_exercise_config')">
                        <template v-slot:activator="{ on, attrs }">
                            <button v-on="on" v-bind="attrs" type="button" class="aed-redo" @click="copyConfig"
                                :disabled="loading || !category">{{ $t('common.copy') }}</button>
                        </template>
                    </button-tooltip>
                    <button-tooltip bottom :tooltip="$t('tooltips.paste_exercise_config')">
                        <template v-slot:activator="{ on, attrs }">
                            <button v-on="on" v-bind="attrs" type="button" class="aed-redo" @click="pasteConfig"
                                :disabled="loading || !category || !exerciseConfigClipboard">{{ $t('common.paste') }}</button>
                        </template>
                    </button-tooltip>
                    <button type="button" class="aed-redo" @click="redo" :disabled="loading">{{ $t('common.redo') }}</button>
                </div>
            </div>

            <!-- BODY -->
            <div v-if="date && !loadingEditMode" class="aed-body">
                <!-- TYPES SIDEBAR (categories) -->
                <div class="aed-types">
                    <div v-if="loadingCategories" class="aed-types-loading">
                        <skeleton-loader type="rows" :count="6" />
                    </div>
                    <template v-else>
                        <button-tooltip v-for="category in categoriesOptions" :key="category.id"
                            :tooltip="categoryLabel(category.name)" right>
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="aed-type"
                                    :class="{ active: selectColorCategory(category) }" v-on="on" v-bind="attrs"
                                    @click="onClickCategory(category)">
                                    <v-icon size="20" :color="selectColorCategory(category) ? 'secondary' : 'txtMuted'"
                                        v-text="category.icon" />
                                </button>
                            </template>
                        </button-tooltip>
                    </template>
                </div>

                <!-- MAIN -->
                <div class="aed-main">
                    <div v-if="!category" class="aed-need-cat">
                        <v-icon size="20" color="secondary" class="mr-2">mdi-alert-circle-outline</v-icon>
                        {{ $t('exercises.need_select_an_category') }}
                    </div>

                    <template v-else>
                        <!-- TOP AREA -->
                        <div class="aed-top">
                            <div class="aed-fields">
                                <!-- ROW 1 -->
                                <div class="aed-row">
                                    <div class="aed-field aed-field-wide">
                                        <kimsa-combobox :label="$t('exercises.types')"
                                            :placeholder="$t('exercises.pick_type')" dense :items="typesOptions"
                                            item-text="label" @change="types = $event" multiple-with-chips
                                            :readonly="!category" :value="types" ref="types" />
                                    </div>
                                    <div class="aed-field aed-field-xs">
                                        <kimsa-combobox :label="$tc('exercises.blocks', 2)"
                                            :placeholder="$t('exercises.pick_block')" dense :items="blocksOptions"
                                            @change="block = $event" :value="block" ref="block" />
                                    </div>
                                    <div class="aed-field aed-field-xs">
                                        <kimsa-combobox :label="$tc('exercises.sets', 2)" :value="set" dense
                                            :items="setOptions" @change="set = $event" ref="set" />
                                    </div>
                                    <div class="aed-field aed-field-pause">
                                        <div class="aed-field-lbl">{{ $t('exercises.pause') }}</div>
                                        <div class="aed-pause">
                                            <button type="button" class="aed-pause-btn" @click="showRestMenu(!!time)">
                                                <template v-if="time">{{ time }}<v-icon size="14"
                                                        class="ml-1">mdi-pencil</v-icon></template>
                                                <v-icon v-else size="18">mdi-plus</v-icon>
                                            </button>
                                            <button v-if="time" type="button" class="aed-pause-del" @click="time = null">
                                                <v-icon size="16" color="error">mdi-delete</v-icon>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <!-- ROW 2 -->
                                <div class="aed-row">
                                    <div class="aed-field aed-field-wide">
                                        <kimsa-combobox can-write @create="showCreateExercise"
                                            :label="$tc('exercises.exercises', 1)"
                                            :placeholder="$t('exercises.pick_exercise')" dense :items="exercisesOptions"
                                            @change="exercise = $event" :value="exercise" item-value="objectID"
                                            ref="exercise" show-video @search="onSearchExercises"
                                            :no-create-exercise="isFreePlan" @showVideo="onShowVideo" no-filter />
                                    </div>
                                    <div v-if="isStrongCategory" class="aed-field aed-field-sm">
                                        <div class="aed-field-lbl">{{ $t('exercises.max_weight') }}</div>
                                        <input type="number" min="0" inputmode="numeric" class="aed-input"
                                            @keypress="onNumberKeypress" v-model="maxWeight" placeholder="—">
                                    </div>
                                    <kimsa-toggle class="aed-toggle-wrap" :value="eachSide" @change="eachSide = $event">
                                        <div>
                                            <div class="aed-toggle-lbl">{{ $t('exercises.each_side') }}</div>
                                            <div class="aed-toggle-sublbl">{{ $t('exercises.each_side_hint') }}</div>
                                        </div>
                                    </kimsa-toggle>
                                </div>
                            </div>

                            <!-- RIGHT PANEL -->
                            <div class="aed-right">
                                <kimsa-toggle class="aed-right-item" label-left :value="painScale" @change="painScale = $event">
                                    <span class="aed-right-lbl">{{ $t('common.pain_scale') }}</span>
                                </kimsa-toggle>
                                <kimsa-toggle v-if="isStrongCategory" class="aed-right-item" label-left :value="weightMode" @change="weightMode = $event">
                                    <span class="aed-right-lbl">{{ $t('common.weight_mode') }}</span>
                                </kimsa-toggle>
                                <div class="aed-right-item">
                                    <span class="aed-right-lbl">{{ $t('common.prescription') }}</span>
                                    <button type="button" class="aed-presc-btn" :class="{ 'is-delete': prescription }"
                                        @click="prescription ? onDeletePrescription() : showPrescriptionSet()">
                                        <v-icon size="14" :color="prescription ? 'error' : '#0C0E12'">
                                            {{ prescription ? '$delete' : 'mdi-plus' }}
                                        </v-icon>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- CONFIG -->
                        <div class="aed-config">
                            <div class="aed-config-title">{{ $t('common.config') }}</div>
                            <series-config-editor ref="config" :category="category" :set.sync="set"
                                :max-weight="maxWeight" :set-max="10" @update:valid="configValid = $event" />
                        </div>

                        <!-- COMMENT -->
                        <div class="aed-comment">
                            <div class="aed-comment-title">{{ $t('common.comment') }}</div>
                            <textarea class="aed-notes" v-model="commentary" :placeholder="$t('common.comment')" />
                        </div>
                    </template>
                </div>
            </div>
            <div v-else class="aed-loading">
                <skeleton-loader type="exercise-editor" />
            </div>

            <!-- FOOTER -->
            <div class="aed-footer">
                <v-btn block tile depressed color="primary" class="aed-save black--text" :disabled="disabledAdd"
                    :loading="loading" @click="add">
                    {{ editMode ? $t('common.save') : $t('common.add') }}
                </v-btn>
            </div>

            <rest-dialog as-menu is-second-dialog @save="time = $event" ref="rest" :value="time" />
            <set-prescription-dialog is-second-dialog @set="onSetPrescription" ref="setPrescription"
                :category="category" :coach-id="coachId" />
            <exercise-creator-desktop-dialog is-second-dialog ref="creator" @success="onSuccessCreate" />
            <watch-video-dialog @hide="onHideVideo" :thumb="currentImg" :video="currentVideo" is-second-dialog
                ref="watchVideo" />
        </div>
    </v-dialog>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { searchExercises } from '@/utils/ExerciseLibrary'
import RestDialog from "@/components/coach/routines/RestDialog";
import { db, FieldValue } from "@/utils/Firebase";
import ExerciseCreatorDesktopDialog from "@/components/coach/routines/ExerciseCreatorDesktopDialog";
import WatchVideoDialog from "@/components/common/routines/WatchVideoDialog";
import { randomString } from "@/utils/Common";
import {
    addAssignedRoutinesGroup, clearBlocksAssignedRoutine,
    deleteAssignedRoutinesGroup, getPayloadBaseAssignedRoutine,
    getPayloadExercise, updateAssignedRoutinesGroup, validateNumberKey
} from "@/utils/Exercises";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import SetPrescriptionDialog from "@/components/coach/routines/SetPrescriptionDialog";
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
import SeriesConfigEditor from "@/components/coach/routines/SeriesConfigEditor";
import { debounce } from "@/utils/Common";

export default {
    name: "AddExerciseDesktopDialog",
    props: {
        currentSelection: {
            type: Object,
            default: null,
        },
        currentRoutines: {
            type: Array,
            default: null,
        },
        collection: {
            type: String,
            default: null,
        },
        assignedRoutineToReplace: {
            type: Array,
            default: () => [],
        },
        coachId: {
            type: String,
            default: null,
        },
        routineNumber: {
            type: Number,
            default: 1,
        },
        noSave: {
            type: Boolean,
            default: false,
        },
        forceBlock: {
            type: Number,
            default: null,
        },
    },
    components: {
        ButtonTooltip,
        SkeletonLoader,
        WatchVideoDialog,
        ExerciseCreatorDesktopDialog, RestDialog, KimsaCombobox,
        SetPrescriptionDialog, KimsaToggle, SeriesConfigEditor
    },
    data(vm) {
        return {
            dialog: false,
            configValid: false,
            firstTime: false,
            editMode: false,
            loadingEditMode: false,
            currentEvent: null,
            date: null,
            loading: false,
            firstEvent: null,
            category: null,
            types: [],
            exercisesOptions: [],
            exercise: null,
            block: 1,
            oldBlock: null,
            lastBlock: null,
            setOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            set: 1,
            time: null,
            eachSide: false,
            painScale: false,
            weightMode: false,
            maxWeight: null,
            commentary: '',
            exerciseIndex: null,
            exerciseUID: null,
            currentImg: null,
            currentVideo: null,
            prescription: null,
            search: '',
        }
    },
    computed: {
        ...mapState(['categoriesOptions', "loadingCategories", 'exerciseConfigClipboard']),
        typesOptions(vm = this) {
            if (!vm.category) return []
            return vm.category.types.map(type => ({ name: type, value: type, label: vm.typeLabel(type) })).sort((a, b) => a.label.localeCompare(b.label))
        },
        isStrongCategory(vm = this) {
            const categoryName = vm.category?.name || ''
            return vm.categoryKey(categoryName) === vm.CATEGORY.STRONG || vm.categoryKey(categoryName) === vm.CATEGORY.STABILITY || vm.categoryKey(categoryName) === vm.CATEGORY.MOBILITY
        },
        blocksOptions(vm = this) {
            let max = 0
            if (vm.forceBlock) {
                vm.block = vm.forceBlock
                return [vm.forceBlock]
            }
            if (vm.currentEvent) {
                max = (!vm.currentEvent.hasMoreExercisesInBlock && vm.currentEvent.lastBlock === vm.currentEvent.block)
                    ? vm.currentEvent.lastBlock - 1
                    : vm.currentEvent.lastBlock
            } else max = vm.firstEvent ? vm.firstEvent.block : (vm.lastBlock || 0)
            let maxBlocks = Array.from({ length: max + 1 }, (_, i) => i + 1)
            const assignedRoutineData = vm.currentRoutines.find(routine => routine.id === (vm.currentEvent?.assignedRoutineId || vm.firstEvent?.assignedRoutineId))
            if (assignedRoutineData) {
                let circuitBlocks = (assignedRoutineData.blocks || [])
                    .map((block, i) => block.isCircuit ? i + 1 : null)
                    .filter(n => n !== null)
                maxBlocks = maxBlocks.filter(block => !circuitBlocks.includes(block))
            }
            if (maxBlocks.length && !maxBlocks.includes(vm.block)) vm.block = maxBlocks[0]
            return maxBlocks
        },
        disabledAdd(vm = this) {
            return !vm.category || !vm.exercise || !vm.block || !vm.set || !vm.configValid
        },
    },
    async mounted() {
        const vm = this
        // console.log(vm)
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        ...mapMutations(['setExerciseConfigClipboard']),
        show(date, firstEvent = null) {
            const vm = this
            vm.date = date
            vm.firstEvent = firstEvent
            if (vm.forceBlock) vm.block = vm.forceBlock
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.date = null
            vm.dialog = false
            vm.category = null
            vm.editMode = false
            vm.loadingEditMode = false
            vm.firstTime = true
            vm.firstEvent = null
            vm.currentEvent = null
            vm.reset()
        },
        reset(noResetBlock = false) {
            const vm = this
            vm.types = []
            vm.exercise = null
            vm.search = ''
            vm.exercisesOptions = []
            if (vm.$refs.exercise) vm.$refs.exercise.search = null
            if (!noResetBlock) vm.block = 1
            vm.set = 1
            vm.time = null
            vm.eachSide = false
            vm.painScale = false
            vm.weightMode = false
            vm.maxWeight = null
            vm.commentary = ''
            vm.prescription = null
        },
        redo() {
            const vm = this
            vm.reset()
            if (vm.$refs.config) vm.$refs.config.reset()
        },
        copyConfig() {
            const vm = this
            if (!vm.$refs.config) return
            vm.setExerciseConfigClipboard(vm.$refs.config.generateConfig())
            vm.toast(vm.$t('common.config_copied'))
        },
        pasteConfig() {
            const vm = this
            if (!vm.$refs.config || !vm.exerciseConfigClipboard) return
            vm.$refs.config.populate(JSON.parse(JSON.stringify(vm.exerciseConfigClipboard)))
            vm.toast(vm.$t('common.config_pasted'))
        },

        async setEditingMode(event) {
            const vm = this
            vm.firstTime = true
            vm.editMode = true
            vm.show(event.date)
            vm.currentEvent = event

            vm.exerciseIndex = event.exerciseIndex
            vm.exerciseUID = event.uid

            const exerciseRef = await vm.withLoading(
                () => db.collection('exercises').doc(event.id).get(),
                'loadingEditMode'
            )

            if (!exerciseRef.exists) return

            vm.exercise = { objectID: exerciseRef.id, ...exerciseRef.data() }
            vm.category = vm.categoriesOptions.find(category => category.name === vm.exercise.categories[0])
            vm.types = vm.exercise.types.map(type => ({ name: type, value: type, label: vm.typeLabel(type) }))
            vm.block = event.block
            vm.oldBlock = event.block
            vm.lastBlock = event.lastBlock
            vm.set = event?.config?.sets.length || null
            vm.time = event.time || ''
            vm.eachSide = event.eachSide || false
            vm.painScale = event.painScale || false
            vm.weightMode = event.weightMode || false
            vm.maxWeight = event.maxWeight || null
            vm.commentary = event.commentary || ''
            vm.prescription = event.prescription || null

            vm.$nextTick(() => {
                if (vm.$refs.config) vm.$refs.config.populate(event.config)
                vm.firstTime = false
            })
        },

        // CATEGORY
        onClickCategory(category) {
            const vm = this
            vm.category = category
        },
        selectColorCategory(category) {
            return this.category?.name === category.name
        },

        // EXERCISES
        async loadExercises() {
            let vm = this
            if (!vm.account) return
            vm.exercisesOptions = []

            const companyId = (vm.account.accountType === 'coach_company' || vm.isCompany)
                ? (vm.account?.coachSubscription?.companyId || vm.currentUser.uid)
                : null

            vm.exercisesOptions = await searchExercises({
                coachId: vm.coachId,
                companyId,
                query: vm.search,
                types: vm.types,
            })
        },
        onSearchExercises(search) {
            const vm = this;
            vm.search = search || ''
            vm.debouncedLoadExercises()
        },
        debouncedLoadExercises: debounce(
            function () {
                this.loadExercises()
            }, 300),
        showCreateExercise(exercise) {
            const vm = this
            let payload = {
                name: exercise,
                types: vm.types?.map(type => type.name) || [],
                categories: [vm.category.name]
            }
            vm.$refs.creator.show(payload)
        },
        async onSuccessCreate(exercise) {
            const vm = this
            // console.log('exerciseCREATED', exercise)
            vm.exercisesOptions.unshift(exercise)
            vm.exercise = exercise
        },
        async onShowVideo(item) {
            const vm = this
            vm.currentImg = item.img || item.video?.thumb || null
            vm.currentVideo = item.video?.url || item?.youtubeUrl || null
            const isYoutubeVideo = item?.youtubeUrl ? true : false
            if (vm.$refs.exercise) vm.$refs.exercise.closeMenu()
            vm.$refs.watchVideo.show(isYoutubeVideo)
        },
        onHideVideo() {
            if (this.$refs.exercise) this.$refs.exercise.openMenu()
        },

        // ADD/EDIT REST
        showRestMenu(edit) {
            const vm = this
            edit ? vm.$refs.rest.showEdit(vm.time) :
                vm.$refs.rest.show()
        },

        // SET PRESCRIPTION
        showPrescriptionSet() {
            const vm = this
            vm.$refs.setPrescription.show()
        },
        onSetPrescription(prescription) {
            const vm = this;
            vm.set = prescription?.config?.sets.length || null
            vm.time = prescription.time || ''
            vm.eachSide = prescription.eachSide || false
            vm.painScale = prescription.painScale || false
            vm.weightMode = prescription.weightMode || false
            vm.maxWeight = prescription.maxWeight || null
            vm.commentary = prescription.commentary || ''
            vm.prescription = prescription
            vm.$nextTick(() => {
                if (vm.$refs.config) vm.$refs.config.populate(prescription.config)
            })
        },
        onDeletePrescription() {
            const vm = this;
            vm.prescription = null
            vm.$nextTick(() => {
                if (vm.$refs.config) vm.$refs.config.populate(vm.currentEvent?.config)
            })
        },

        onNumberKeypress(event) {
            validateNumberKey(event)
        },

        // ADD/EDIT
        generateExercise(config) {
            const vm = this
            let exercise = {
                id: vm.exercise.objectID,
                objectID: vm.exercise.objectID,
                type: 'exercise',
                name: vm.exercise.name,
                commentary: vm.commentary,
                config: config,
                eachSide: vm.eachSide,
                painScale: vm.painScale,
                weightMode: vm.weightMode,
                hasTime: !!vm.time,
                time: vm.time,
            }
            if (vm.prescription && vm.prescription.weeks > 1) {
                exercise.prescription = vm.prescription.objectID
                exercise.currentWeek = 1
            }
            if (vm.isStrongCategory) exercise.maxWeight = vm.maxWeight

            return exercise
        },
        async addNewAssignedRoutine(blocks) {
            const vm = this
            let newBlocks = clearBlocksAssignedRoutine(blocks)

            const name = `${vm.currentSelection.name} - ${vm.$moment(vm.date).format('DD-MM-YYYY')}`
            let payloadAssignedRoutine = getPayloadBaseAssignedRoutine(name, vm.date, { coachId: vm.coachId }, blocks, vm.routineNumber)

            if (vm.collection === 'assignedRoutines') {
                payloadAssignedRoutine.studentId = vm.currentSelection.objectID
            } else {
                payloadAssignedRoutine.teamId = vm.currentSelection.teamId || null
                payloadAssignedRoutine.groupId = vm.currentSelection.id
            }

            let maxBlock = newBlocks.length

            const arRef = await db.collection(vm.collection).add(payloadAssignedRoutine)
            const assignedRoutineId = arRef.id

            if (vm.collection === 'groupAssignedRoutines') {
                let students = [...vm.currentSelection.students]
                if (vm.assignedRoutineToReplace.length > 0) {
                    let toDelete = vm.assignedRoutineToReplace.filter(ar => ar.replace).map(ar => {
                        return ar.assignedRoutineId
                    })
                    if (toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)
                    let skippedStudents = vm.assignedRoutineToReplace.filter(ar => !ar.replace).map(ar => {
                        return ar.studentId
                    })
                    if (skippedStudents.length > 0) students = students.filter(st => !skippedStudents.includes(st))
                }
                await addAssignedRoutinesGroup({ id: assignedRoutineId, ...payloadAssignedRoutine, date: vm.date }, students)
            }

            return { assignedRoutineId, assignedRoutine: payloadAssignedRoutine, maxBlock }
        },
        async addExerciseInAssignedRoutine(assignedRoutineId, exercise) {
            const vm = this
            const assignedRoutineData = vm.currentRoutines.find(routine => routine.id === assignedRoutineId)
            if (!assignedRoutineData) {
                vm.loading = false
                vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
                return null
            }
            exercise = getPayloadExercise(exercise, false)
            let blocks = clearBlocksAssignedRoutine(assignedRoutineData.blocks, false)

            if (vm.editMode) {
                if (!blocks[vm.block - 1]) {
                    // NEW BLOCK
                    blocks[vm.oldBlock - 1].exercises.splice(vm.exerciseIndex, 1)
                    blocks.push({ id: vm.block, exercises: [exercise] })
                } else {
                    // OLD BLOCK CHANGE
                    if (vm.oldBlock === vm.block) {
                        // REPLACE IN SAME BLOCK
                        blocks[vm.block - 1].exercises.splice(vm.exerciseIndex, 1, exercise)
                    } else {
                        // CHANGE IN EXISTS BLOCK
                        blocks[vm.block - 1].exercises.push(exercise)
                        blocks[vm.oldBlock - 1].exercises.splice(vm.exerciseIndex, 1)
                        if (blocks[vm.oldBlock - 1].exercises.length === 0) blocks.splice(vm.oldBlock - 1, 1)
                    }
                }
            } else {
                !blocks[vm.block - 1] ?
                    blocks.push({ id: vm.block, exercises: [exercise] }) :
                    blocks[vm.block - 1].exercises.push(exercise)
            }

            const maxBlock = blocks.length

            await db.collection(vm.collection).doc(assignedRoutineId).update({ blocks: blocks })

            if (vm.collection === 'groupAssignedRoutines') await updateAssignedRoutinesGroup(assignedRoutineData.assignedRoutinesIds, blocks)

            return { maxBlock }
        },
        async add() {
            const vm = this
            await vm.withLoading(async () => {
            let assignedRoutineId = null
            let maxBlock = null

            // SET CONFIG
            const config = vm.$refs.config.generateConfig()
            let exercise = vm.generateExercise(config)

            let exercises = [{ ...exercise }]

            if (vm.noSave) {
                vm.$emit('success', [{ ...vm.exercise, ...exercise }])
                vm.hide()
                return
            }

            if (!vm.editMode && !vm.firstEvent) {
                let blocks = [{ id: vm.block, exercises: exercises }]
                let payload = await vm.addNewAssignedRoutine(blocks)
                assignedRoutineId = payload.assignedRoutineId
                maxBlock = payload.maxBlock
            } else {
                assignedRoutineId = vm.currentEvent?.assignedRoutineId || vm.firstEvent?.assignedRoutineId
                let payload = await vm.addExerciseInAssignedRoutine(assignedRoutineId, exercise)
                if (!payload) return
                maxBlock = payload.maxBlock
            }

            let payloadEmit = {
                name: exercise.name || vm.$tc('exercises.exercises', 1),
                start: vm.date,
                block: vm.block,
                date: vm.date,
                assignedRoutineId: assignedRoutineId,
                isLastExercise: false,
                editExercise: false,
                uid: vm.exerciseUID || randomString(10, '', 'ex'),
                ...exercise
            }

            if (vm.prescription?.objectID) {
                await db.collection('prescriptions').doc(vm.prescription.objectID).update({ used: FieldValue.increment(1) })
            }

            if (vm.editMode) {
                payloadEmit.editExercise = true
                vm.hide()
            }

            vm.$emit('success', payloadEmit)
            vm.toast(vm.$t('exercises.saved'))
            vm.category = null
            vm.editMode = false
            vm.firstTime = true
            vm.firstEvent = {
                assignedRoutineId: assignedRoutineId,
                block: maxBlock
            }
            vm.reset()
            })
        },
    },
    watch: {
        types() {
            const vm = this
            if (!vm.firstTime) vm.exercise = null
            vm.loadExercises()
        },
        category(val, oldVal) {
            const vm = this
            if (oldVal !== null) vm.reset(true)
        },
    }
}
</script>

<style>
.aed-dialog {
    box-shadow: none !important;
    border-radius: 16px;
    overflow: visible;
}
</style>

<style scoped>
.aed-modal {
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 16px;
    overflow: hidden;
}

.aed-hdr {
    height: 52px;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 0 24px;
    background: var(--v-bg-base);
    border-bottom: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.aed-close {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1.5px solid var(--v-txtMuted-base);
    background: transparent;
    color: var(--v-txtMuted-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: border-color .15s, color .15s;
}

.aed-close:hover {
    border-color: var(--v-txtPrimary-base);
    color: var(--v-txtPrimary-base);
}

.aed-title {
    flex: 1;
    font-size: 15px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.aed-hdr-actions {
    display: flex;
    align-items: center;
    gap: 14px;
}

.aed-redo {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--v-accent-base);
    background: transparent;
    border: none;
    cursor: pointer;
}

.aed-redo:disabled {
    opacity: 0.4;
    cursor: default;
}

.aed-body {
    flex: 1;
    display: flex;
    min-height: 0;
    overflow: hidden;
}

.aed-types {
    width: 56px;
    flex-shrink: 0;
    background: var(--v-bg-base);
    border-right: 1px solid var(--v-outline-base);
    display: flex;
    flex-direction: column;
    padding: 12px 0;
    gap: 2px;
    overflow: hidden;
}

.aed-types-loading {
    display: flex;
    justify-content: center;
    padding: 12px 0;
}

.aed-type {
    height: 44px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    transition: background 0.15s;
}

.aed-type::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    bottom: 8px;
    width: 2px;
    border-radius: 0 2px 2px 0;
    background: var(--brand-gradient);
    opacity: 0;
    transition: opacity 0.15s;
}

.aed-type.active::before {
    opacity: 1;
}

.aed-type.active {
    background: rgba(102, 204, 204, 0.08);
}

.aed-type:hover {
    background: rgba(102, 204, 204, 0.05);
}

.aed-type .v-icon {
    opacity: 0.55;
    transition: opacity 0.15s;
}

.aed-type.active .v-icon,
.aed-type:hover .v-icon {
    opacity: 1;
}

.aed-main {
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.aed-need-cat {
    align-self: center;
    margin-top: auto;
    margin-bottom: auto;
    max-width: 480px;
    display: flex;
    align-items: center;
    padding: 14px 16px;
    border-radius: 10px;
    background: rgba(102, 204, 204, 0.08);
    border: 1px solid var(--v-secondary-base);
    color: var(--v-txtSecondary-base);
    font-size: 13px;
}

.aed-top {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    flex-shrink: 0;
}

.aed-fields {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.aed-row {
    display: flex;
    align-items: flex-end;
    gap: 10px;
}

.aed-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.aed-field-wide {
    width: 230px;
    flex-shrink: 0;
}

.aed-field-sm {
    width: 96px;
    flex-shrink: 0;
}

.aed-field-pause {
    width: auto;
    flex-shrink: 0;
}

.aed-field-xs {
    width: 74px;
    flex-shrink: 0;
}

.aed-field-lbl {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.aed-input {
    height: 38px;
    width: 100%;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    outline: none;
}

.aed-input:focus {
    border-color: var(--v-accent-base);
}

.aed-pause {
    display: flex;
    align-items: center;
    gap: 6px;
}

.aed-pause-btn {
    height: 38px;
    min-width: 64px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    color: var(--v-txtPrimary-base);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.aed-pause-btn:hover {
    border-color: var(--v-accent-base);
}

.aed-pause-del {
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
}

.aed-toggle-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 40px;
    flex-shrink: 0;
}

.aed-toggle-lbl {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.aed-toggle-sublbl {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.aed-right {
    width: 224px;
    flex-shrink: 0;
    background: var(--v-bg-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.aed-right-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.aed-right-lbl {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
}

.aed-presc-btn {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    border: none;
    background: var(--brand-gradient);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
}

.aed-presc-btn.is-delete {
    background: rgba(232, 92, 92, 0.15);
}

.aed-config {
    flex: 0 0 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.aed-config-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    padding-bottom: 8px;
    border-bottom: 1px solid var(--v-outline-base);
    margin-bottom: 12px;
}

.aed-comment {
    flex: 0 0 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.aed-comment-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    padding-bottom: 8px;
}

.aed-config-scroll {
    flex: 1;
    min-height: 0;
    overflow: auto;
}

.aed-config-scroll::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.aed-config-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.aed-config-scroll::-webkit-scrollbar-thumb {
    background: var(--v-outline2-base);
    border-radius: 4px;
}

.aed-config-scroll::-webkit-scrollbar-corner {
    background: transparent;
}

.aed-config-head {
    display: flex;
    gap: 8px;
    width: max-content;
    align-items: flex-end;
    position: sticky;
    top: 0;
    z-index: 3;
    background: var(--v-surface-base);
    padding-right: 14px;
}

.aed-cfg-corner {
    box-sizing: border-box;
    width: 130px;
    padding-right: 12px;
    flex-shrink: 0;
    align-self: stretch;
    position: sticky;
    left: 0;
    z-index: 4;
    background: var(--v-surface-base);
    border-right: 1px solid var(--v-outline-base);
}

.aed-set-hd {
    width: 60px;
    flex-shrink: 0;
    text-align: center;
    font-size: 9px;
    font-weight: 500;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    margin-bottom: 6px;
}

.aed-config-row {
    display: flex;
    gap: 8px;
    width: max-content;
    align-items: stretch;
    padding-right: 14px;
}

.aed-cfg-cell {
    box-sizing: border-box;
    width: 130px;
    padding-right: 12px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    min-height: 46px;
    position: sticky;
    left: 0;
    z-index: 2;
    background: var(--v-surface-base);
    border-right: 1px solid var(--v-outline-base);
}

.aed-cfg-type {
    width: 100%;
}

.aed-cfg-type >>> .v-select__selection--comma {
    margin: 0 !important;
}

.aed-cfg-type >>> .v-select__selections input {
    text-align: left;
}

.aed-cfg-type >>> .v-input__control,
.aed-cfg-type >>> .v-input__slot {
    min-height: 40px !important;
    height: 40px;
}

.aed-cfg-type >>> .v-input__slot {
    border-radius: 8px;
}

.aed-cfg-type >>> .v-input__append-inner {
    align-self: center;
    margin-top: 0 !important;
}

.aed-cfg-type >>> .v-input__icon--clear .v-icon {
    font-size: 18px !important;
}

.aed-cfg-static {
    height: 40px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 0 12px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    color: var(--v-txtPrimary-base);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

.aed-cfg-static .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.aed-cfg-static--plain {
    cursor: default;
}

.aed-sets {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
    align-items: center;
}

.aed-set-input {
    width: 60px;
    height: 34px;
    flex-shrink: 0;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 7px;
    padding: 0 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    text-align: center;
    outline: none;
}

.aed-set-input:focus {
    border-color: var(--v-accent-base);
}

.aed-set-input::-webkit-outer-spin-button,
.aed-set-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.aed-set-input[type=number] {
    -moz-appearance: textfield;
}

.aed-notes {
    width: 100%;
    min-height: 68px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    padding: 10px 12px;
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    resize: vertical;
    outline: none;
}

.aed-notes:focus {
    border-color: var(--v-accent-base);
}

.aed-loading {
    flex: 1;
    display: flex;
    min-height: 0;
    overflow: hidden;
}

.aed-footer {
    flex-shrink: 0;
    border-top: 1px solid var(--v-outline-base);
}

.aed-save {
    height: 52px !important;
    font-weight: 700;
    letter-spacing: normal;
}
</style>